import { UIElement } from '../ui-element';
import { createElement, insertElements } from '../../global/render-api';
import { Labels } from '../../global/labels';
import { UIAspect } from '../aspect/ui-aspect';
import { UIButtonbar } from '../buttonbar/ui-buttonbar';
import { UIIcon } from '../icon/ui-icon';
import { createPictureWithSources, Mapped } from '../../global/ui-helpers';
import { browser } from '../../global/index';
import { UISurfaceColors } from '../../global/color';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child, Children } from '../../decorators/child.decorator';
import type { TranslationLabels } from '../../types/labels.type';
import { isElementNode } from '../../global/helpers';
import type { Attributes, ElementChildren } from '../ui-element.type';
import type { EventHandler } from '../../global/utils.type';
import type { AspectRatioInput } from '../aspect/ui-aspect.type';
import stylesOfferIntroduction from './ui-offer-introduction.css';
import stylesOfferLogout from './ui-offer-logout.css';
import stylesOfferPersonal from './ui-offer-personal.css';
import stylesPega from './ui-offer-pega.css';
import stylesSection from './ui-offer-section.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIOffer
 * @element ui-offer
 * @classdesc Represents a class for <code>ui-offer</code> element.
 * Element for marketing offer in general. On close component fires event
 * 'offer-close'.
 * @fires event:offer-close
 * @property {UIOfferLayouts} [layout="introduction"] {@attr layout} Layout of offer.
 * @property {string} [image] {@attr image} Proxy image attribute for &lt;figure> tag.
 * @property {string} [imageMobile] {@attr image-mobile} Proxy image attribute for &lt;figure> tag.
 * @property {string} [alt] {@attr alt} Alternate text for image same as 'alt=""'.
 * @property {string} [aspectRatio] {@attr aspect-ratio} Aspect ratio for image, format "width:height", e.g. "16:9".
 * Not usable with 'pega' and 'pega-tip' layouts.
 * @property {string} [category] {@attr category} Category of personal offer.
 * Only for layout="personal".
 * @property {boolean} [closable] {@attr closable} add close functionality for offers.
 * Only for layout="personal".
 * @property {boolean} [imageNocrop] {@attr image-nocrop} Adds spacing between the image and the edge of the offer.
 * Works only for introduction, section, and logout layouts.
 * @property {boolean} [hideMobileImage] {@attr hide-mobile-image} Hides the image on mobile devices for logout offers.
 * @property {boolean} [disableSurface] {@attr disable-surface} Disables surface color and removes padding.
 * Only for layout="section".
 * @property {boolean} [livingOak] {@attr living-oak} Toggles living oak mask styles for image.
 * Only for layout="introduction" and layout="section".
 * @property {string} [slogan] {@attr slogan} Slogan text to be displayed on top of the image.
 * Only for layout="introduction".
 * @property {string} [overline] {@attr overline} Overline text to be displayed above the title.
 * Only for layout="introduction" and layout="section" and layout="personal".
 * @property {"left" | "right"} [imagePosition] {@attr image-position} Position of the image
 *  {@desc left}
 *  {@desc right}
 * Only for layout="section".
 * @property {UISurfaceColor} [background] {@attr background} Sets background surface color for the offer.
 *  {@desc neutral: Neutral color - Default}
 *  {@desc primary: Primary color}
 *  {@desc secondary: Secondary color}
 *  {@desc tertiary: Tertiary color}
 *  {@desc outlined: Outlined color}
 * @property {string} [offerId] {@attr offer-id} Offer's ID for closable functionality
 * it will be sent to back system of ibank or any other.
 * @property {string} [offerStatus] {@attr offer-status} Offer's status for closable
 * functionality it will be sent to back system of ibank or any other system.
 * @property {UIButtonbar} buttonbar Shortcut to button bar inside offer if exists.
 * @property {HTMLButtonElement|null} closeBtn Shortcut to close button if offer have
 * 'closable' attribute.
 * @property {HTMLElement[]|null} disclaimers Shortcut to all elements with
 * .disclaimer or .disclaimer-lt CSS classes.
 * @property {HTMLElement|null} article Shortcut to article element.
 * @property {HTMLElement|null} figure Shortcut to figure element.
 * @slot
 * @example
 * <ui-offer layout="personal">
 *   <figure>
 *     <img src="assets/mocks/images/offer06.jpg" alt="Discount">
 *   </figure>
 *   <article>
 *     <h3>Only today is discount 20%</h2>
 *     <p>
 *       Travel insurance only today 20 percent cheaper.
 *     </p>
 *     <ui-buttonbar alignment="left">
 *       <ui-button>Apply</ui-button>
 *     </ui-buttonbar>
 *   </article>
 * </ui-offer>
 * @example <caption>or shorten version</caption>
 * <ui-offer layout="personal" image="/proinfo-files/discount.jpg">
 *   <h2>Only today is discount 20%</h2>
 *   <p>
 *     Travel insurance only today 20 percent cheaper.
 *   </p>
 *   <ui-buttonbar>
 *     <button class="button">Apply</a>
 *   </ui-buttonbar>
 * </ui-offer>
 */
@Component(
    {
        tag: 'ui-offer',
        styles: [
            ...stylesOfferIntroduction,
            ...stylesOfferPersonal,
            ...stylesPega,
            ...stylesSection,
            ...stylesOfferLogout,
        ],
    },
    Mapped.True
)
export class UIOffer extends UIElement {
    /**
     * @type {UIOfferLayouts}
     */
    static readonly Layout = {
        Introduction: 'introduction',
        Personal: 'personal',
        Pega: 'pega',
        PegaTip: 'pega-tip',
        Logout: 'logout',
        Section: 'section',
    };

    /**
     * @type {{"left": string, "right": string}}
     */
    static readonly ImagePosition = {
        Left: 'left',
        Right: 'right',
    };

    static readonly CloseEventName = 'offer-close';

    @Attribute(String, Mapped.Image) image: string = '';
    @Attribute(String, Mapped.Alt) alt: string = '';
    @Attribute(String) category: string = '';
    @Attribute(String, Mapped.True) layout = UIOffer.Layout.Introduction;
    @Attribute(Boolean) imageNocrop?: boolean;
    @Attribute(String, Mapped.Image) imageMobile?: string;
    @Attribute(String, Mapped.True) imagePosition?: string;
    @Attribute(Boolean) disableSurface?: boolean;
    @Attribute(Boolean, Mapped.True) closable?: boolean;
    @Attribute(String) offerId?: string;
    @Attribute(String) background?: string;
    @Attribute(String) offerStatus?: string;
    @Attribute(Boolean, Mapped.True) livingOak?: boolean;
    @Attribute(String) slogan?: string;
    @Attribute(String) overline?: string;
    @Attribute(String) aspectRatio?: AspectRatioInput;
    @Attribute(Boolean) hideMobileImage?: boolean;

    @Child('ui-buttonbar') buttonbar!: UIButtonbar;
    @Child('.ui-offer__close-button') closeBtn?: HTMLButtonElement;
    @Child(':scope > figure') figure?: HTMLElement;
    @Child(':scope > article') article?: HTMLElement;
    @Children('.disclaimer, .disclaimer-lt')
    disclaimers!: NodeListOf<HTMLElement>;

    private onCloseHandler!: EventHandler;

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-offer', {
            dismissOffer: 'Dismiss offer',
        });
    }

    static readonly FigureElementsSelector = [
        'ui-shape',
        ':scope > img',
        'picture',
        'ui-video',
    ].join(',');

    static readonly ButtonPriority = {
        PrimaryButton: 0,
        SecondaryButton: 1,
        TertiaryButton: 2,
        Link: 3,
    };

    /**
     * Creates HTML for category section.
     * @private
     * @returns {HTMLDivElement}
     */
    private createCategoryElem(): HTMLDivElement {
        return createElement<HTMLDivElement>({
            tagName: 'div',
            classList: {
                'ui-offer__category': true,
            },
            children: [this.category],
        })!;
    }

    /**
     * Returns a classlist object containing living oak mask classes.
     * @returns {Record<string, boolean> | undefined}
     * @private
     */
    private getLivingOakMaskClasses(): Record<string, boolean> | undefined {
        if (!this.livingOak) {
            return;
        }

        const isMaskLeft = this.imagePosition !== 'left';
        const isMaskRight = this.imagePosition === 'left';

        return {
            '-living-oak-mask': true,
            '-living-oak-mask-left': isMaskLeft,
            '-living-oak-mask-right': isMaskRight,
            '-living-oak-mask-tablet-bottom': true,
        };
    }

    /**
     * Creates layout from given attributes.
     * @private
     */
    private createOffer(): void {
        this.positionButtons();
        const sources = [];
        let shouldHavePicture = false;

        if (this.isRedesignLayout()) {
            shouldHavePicture = true;
            if (this.imageMobile) {
                sources.push({
                    media: browser.getMediaQueryMap().Mobile,
                    srcset: this.imageMobile,
                });
            }
        }

        let overline;
        if (this.overline) {
            overline = this.createElement({
                tagName: 'strong',
                classList: {
                    overline: true,
                },
                children: [this.overline],
            });
        }

        let article = this.querySelector('article');
        const badge = this.querySelector('ui-badge');
        const remark = this.querySelector('ui-remark');
        if (!article) {
            article = this.createElement({
                tagName: 'article',
                children: [overline, ...this.childNodes].filter(
                    (node) =>
                        node &&
                        isElementNode(node) &&
                        !['figure', 'img'].includes(node.localName)
                ) as ElementChildren,
            });
            this.insertAdjacentElement('beforeend', article);
        } else if (overline) {
            article.prepend(overline);
        }

        if (this.image) {
            let sloganContainer = null;
            if (this.slogan) {
                const slogan = this.createElement({
                    tagName: 'span',
                    children: [this.slogan],
                });

                sloganContainer = this.createElement({
                    tagName: 'h3',
                    classList: {
                        'ui-offer__slogan': true,
                    },
                    children: [slogan],
                });
            }

            this.insertBefore(
                this.createElement({
                    tagName: 'figure',
                    children: [
                        createPictureWithSources(
                            sources,
                            this.image,
                            this.alt,
                            shouldHavePicture,
                            this.getLivingOakMaskClasses()
                        ),
                        remark,
                        badge,
                        sloganContainer,
                    ],
                }),
                article
            );
        }

        // If user does not wrap them in <figure> tag
        const figureElems = [
            ...this.querySelectorAll(UIOffer.FigureElementsSelector),
        ];
        const figure = this.figure ?? document.createElement('figure');
        figureElems.forEach((element) => {
            const parentElement = element.parentElement;
            if (
                parentElement &&
                !['figure', 'picture'].includes(parentElement.localName)
            ) {
                badge && figureElems.push(badge);
                remark && figureElems.push(remark);
                figure.append(...figureElems);
            }
        });
        if (figure.children.length > 0) {
            this.insertBefore(figure, article);
        }

        if (this.closable) {
            const actionsContainer = this.createElement({
                tagName: 'div',
                classList: {
                    'ui-offer__actions': true,
                },
                children: [
                    {
                        tagName: 'button',
                        attributes: {
                            type: 'button',
                            title: UIOffer.labels.dismissOffer,
                        },
                        classList: {
                            'ui-offer__close-button': true,
                            '-iconed': true,
                        },
                        children: [
                            {
                                tagName: 'ui-icon',
                                attributes: {
                                    glyph: this.isPersonalOffer()
                                        ? 'redesign-x-mark'
                                        : 'cross',
                                    color: UIIcon.colors.DEFAULT,
                                },
                            },
                        ],
                    },
                ],
            });
            this.prepend(actionsContainer);
        }

        if (this.category) {
            article.insertAdjacentElement(
                'afterbegin',
                this.createCategoryElem()
            );
        }

        const shouldDisplaceDisclaimers =
            this.disclaimers.length > 0 &&
            (this.isIntroductionOffer() || this.isSectionOffer());
        if (shouldDisplaceDisclaimers) {
            [].forEach.call(this.disclaimers, (disclaimer) => {
                article.insertAdjacentElement('afterend', disclaimer);
            });
        }

        const isOutlined = this.background === UISurfaceColors.Outlined;
        const isOutlinedLayout =
            this.isIntroductionOffer() || this.isSectionOffer();
        if (isOutlinedLayout && isOutlined) {
            const outlineContainer = this.createElement({
                tagName: 'div',
                classList: {
                    'outline-container': true,
                },
            });
            this.prepend(outlineContainer);
        }

        // Add inner div wrapper around all children if needed
        if (this.isPegaOffer() || this.isPegaTip()) {
            const innerWrapper = this.createElement({
                tagName: 'div',
                classList: { 'ui-offer__inner-wrapper': true },
            });
            // Move all existing children into the wrapper
            innerWrapper.append(...this.children);
            this.appendChild(innerWrapper);
        }
    }

    /**
     * Updates attributes on component with default values.
     * @private
     */
    private setDefaultAttributes(): void {
        const attributes: Attributes = {};

        if (this.layout === UIOffer.Layout.Introduction) {
            attributes.layout = UIOffer.Layout.Introduction;
            /* TODO: During fff-ish migration(task GUI-4224), add tertiary color as default */
        }

        this.setAttributes(attributes);
    }

    /**
     * @private
     * @returns {boolean}
     */
    private isRedesignLayout(): boolean {
        return [
            UIOffer.Layout.Introduction,
            UIOffer.Layout.Personal,
            UIOffer.Layout.Pega,
            UIOffer.Layout.PegaTip,
            UIOffer.Layout.Section,
        ].includes(this.layout);
    }

    /**
     * @private
     * @returns {boolean}
     */
    private canBeClosed(): boolean {
        return !!(this.closable && this.closeBtn);
    }

    /**
     * @private
     * @returns {boolean}
     */
    private isIntroductionOffer(): boolean {
        return this.layout === UIOffer.Layout.Introduction;
    }

    /**
     * @private
     * @returns {boolean}
     */
    private isLogoutOffer(): boolean {
        return this.layout === UIOffer.Layout.Logout;
    }

    /**
     * @private
     * @returns {boolean}
     */
    private isSectionOffer(): boolean {
        return this.layout === UIOffer.Layout.Section;
    }

    /**
     * @private
     * @returns {boolean}
     */
    private isPersonalOffer(): boolean {
        return this.layout === UIOffer.Layout.Personal;
    }

    /**
     * @private
     * @returns {boolean}
     */
    private isPegaOffer(): boolean {
        return this.layout === UIOffer.Layout.Pega;
    }

    /**
     * @private
     * @returns {boolean}
     */
    private isPegaTip(): boolean {
        return this.layout === UIOffer.Layout.PegaTip;
    }

    /**
     * Position buttons inside button-bar correctly.
     * @private
     */
    private positionButtons(): void {
        if (!this.buttonbar) {
            return;
        }
        const buttonsAndLinks = this.buttonbar.querySelectorAll<
            HTMLButtonElement | HTMLAnchorElement
        >('button, a');
        if (
            this.isIntroductionOffer() ||
            this.isPersonalOffer() ||
            this.isPegaOffer() ||
            this.isPegaTip() ||
            this.isSectionOffer()
        ) {
            if (
                !this.buttonbar.alignment &&
                !this.buttonbar.hasAttribute('alignment')
            ) {
                this.buttonbar.alignment = UIButtonbar.Alignment.Left;
            }
            buttonsAndLinks.forEach((item) => item.classList.add('-left'));
            if (
                this.isPersonalOffer() &&
                this.buttonbar.alignment === UIButtonbar.Alignment.Left
            ) {
                this.sortButtonbar();
            }
        }
    }

    /**
     * Reorders legacy buttons and links in buttonbar.
     * Elements are sorted in the following order:
     * 1) primary buttons 2) secondary (guiding) buttons 3) tertiary (destructive) buttons 4) <a> links.
     * @private
     * @returns {void}
     */
    private sortButtonbar(): void {
        // mobile's 'flex-direction: column-reverse' would give wrong results
        if (browser.mobileMediaMatches()) {
            return;
        }
        const priority = (el: Element) => {
            if (el.localName === 'a') {
                return UIOffer.ButtonPriority.Link;
            }
            if (el.classList.contains('-destructive')) {
                return UIOffer.ButtonPriority.TertiaryButton;
            }
            if (el.classList.contains('-guiding')) {
                return UIOffer.ButtonPriority.SecondaryButton;
            }
            return UIOffer.ButtonPriority.PrimaryButton;
        };

        const actions = Array.from<HTMLButtonElement | HTMLAnchorElement>(
            this.buttonbar.querySelectorAll(':scope > button, :scope > a')
        ).sort((a, b) => priority(a) - priority(b));

        insertElements(this.buttonbar, actions);
    }

    /**
     * When offer closed.
     * @private
     */
    private onClose(): void {
        this.dispatchCustomEvent(UIOffer.CloseEventName, {
            id: this.offerId,
            status: this.offerStatus,
        });
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.createOffer();
        this.setDefaultAttributes();
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        const image = this.figure?.querySelector('img');
        const isValidLayout =
            this.isIntroductionOffer() ||
            this.isPersonalOffer() ||
            this.isSectionOffer() ||
            this.isLogoutOffer();
        if (image && this.aspectRatio && isValidLayout) {
            UIAspect.applyAspectRatio(image, this.aspectRatio);
        }

        if (this.canBeClosed()) {
            this.onCloseHandler = this.onClose.bind(this);
            this.closeBtn?.addEventListener('click', this.onCloseHandler);
        }
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        if (this.canBeClosed()) {
            this.closeBtn?.removeEventListener('click', this.onCloseHandler);
        }
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        if (this.canBeClosed()) {
            this.onCloseHandler = this.onClose.bind(this);
            this.closeBtn?.addEventListener('click', this.onCloseHandler);
        }
    }
}
